import { Component, OnChanges, SimpleChanges, inject, input } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../../forms/ziro-form-abstract.directive';
import { ZiroRadioConfig } from '../../../../../forms/fields/radio/ziro-radio.component';
import {
  ZiroFieldComponentConfig,
  ZiroFormConfig,
  ZiroFormData,
  ZiroFormsValidationState,
} from '../../../../../forms/ziro-forms-models';
import { HtmlInputType, ZiroTextConfig } from '../../../../../forms/fields/text/ziro-text.component';
import { TranslateService } from '@ngx-translate/core';
import { ZiroIcons } from '../../../../../shared/models/ziro-icons.enum';
import { ButtonStyles } from '../../../../../button/button.component';
import { Observable, of } from 'rxjs';
import { ZiroFormStateService } from '../../../../../forms/ziro-form-state.service';

enum LdapProtocol {
  LDAP = 'ldap',
  LDAPS = 'ldaps',
}

interface AdminLdapManagementSettingsLdapServerUrlFormData extends ZiroFieldComponentConfig {
  hostAddress: string;
  protocol: LdapProtocol;
  port: string;
}

@Component({
  selector: 'ziro-admin-ldap-management-settings-ldap-server-url-form',
  templateUrl: './admin-ldap-management-settings-ldap-server-url-form.component.html',
  standalone: false,
})
export class AdminLdapManagementSettingsLdapServerUrlFormComponent
  extends ZiroFormAbstractDirective<string, AdminLdapManagementSettingsLdapServerUrlFormData>
  implements OnChanges
{
  private translateService = inject(TranslateService);
  protected _ziroFormStateService: ZiroFormStateService;

  readonly isParentFormSubmitted = input<boolean>();

  ziroIcons = ZiroIcons;
  buttonStyles = ButtonStyles;
  formConfig = {
    fields: {
      hostAddress: {
        label: 'tkey;admin.ldap.settings.servers.host.label',
        dataAutomation: 'admin-ldap-management-server-host-address',
        required: true,
      },
      protocol: {
        label: 'tkey;admin.ldap.settings.servers.protocol.label',
        componentConfig: new ZiroRadioConfig<string>({
          buttons: [
            {
              value: LdapProtocol.LDAP,
              label: 'LDAP',
            },
            {
              value: LdapProtocol.LDAPS,
              label: 'LDAPS',
            },
          ],
          inline: true,
        }),
        dataAutomation: 'admin-ldap-management-server-protocol',
        required: true,
      },
      port: {
        label: 'tkey;admin.ldap.settings.servers.port.label',
        dataAutomation: 'admin-ldap-management-server-port',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER }),
        required: true,
        validation: [
          {
            validator: (val: number) => (val >= 1 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID),
            message: this.translateService.instant('tkey;validators.global.error.min', {
              minValue: 1,
            }),
          },
          {
            validator: (val: number) =>
              val <= 11999 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
            message: this.translateService.instant('tkey;validators.global.error.max', {
              maxValue: 11999,
            }),
          },
        ],
      },
    },
  } as ZiroFormConfig;

  protected submit(): Observable<null> {
    return of(null);
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (
      changes.isParentFormSubmitted &&
      changes.isParentFormSubmitted.currentValue &&
      changes.isParentFormSubmitted.currentValue !== changes.isParentFormSubmitted.previousValue
    ) {
      this._validateAndSubmitSource.next(true);
    }
  }

  toFormData = (entity: string): AdminLdapManagementSettingsLdapServerUrlFormData => {
    const match = entity.match(/(ldaps?:\/\/)?(.*?):(.*)/);
    const [protocolStr, hostAddress, port] = (match || []).slice(1);
    const protocol = protocolStr ? (protocolStr === 'ldaps://' ? LdapProtocol.LDAPS : LdapProtocol.LDAP) : null;
    return {
      hostAddress,
      protocol,
      port,
    };
  };

  toEntity = (formData: ZiroFormData): string => {
    const protocolPrefix =
      formData.protocol === '' ? '' : formData.protocol === LdapProtocol.LDAPS ? 'ldaps://' : 'ldap://';
    if (formData.port !== '389' && formData.port !== '636') {
      return `${protocolPrefix}${formData.hostAddress.trim() || ''}:${formData.port}`;
    }
    return `${protocolPrefix}${formData.hostAddress.trim() || ''}:${
      formData.protocol === LdapProtocol.LDAPS ? '636' : '389'
    }`;
  };
}
